import { MutationCache, QueryClient } from '@tanstack/react-query'
import type { ServiceDict } from '~/API/externals'

import { getInvalidationHandler } from './mutationInvalidationMap'

let queryClient: QueryClient

/**
 * Get or create the singleton QueryClient instance.
 *
 * The QueryClient is configured with a MutationCache that automatically
 * invalidates queries based on the MUTATION_INVALIDATION_MAP when mutations succeed.
 *
 * @see ./mutationInvalidationMap.ts for the mutation → query invalidation rules
 */
export function getQueryClient() {
    if (queryClient === undefined) {
        queryClient = new QueryClient({
            /**
             * MutationCache with centralized invalidation logic.
             *
             * When any mutation succeeds, this callback checks if there's a
             * corresponding invalidation rule in MUTATION_INVALIDATION_MAP.
             * If found, it automatically invalidates the related queries.
             *
             * This ensures cache consistency without requiring every mutation
             * call site to manually invalidate queries.
             */
            mutationCache: new MutationCache({
                onSuccess: (_data, variables, _context, mutation) => {
                    const handler = getInvalidationHandler(mutation.options.mutationKey)
                    if (handler) {
                        void handler(queryClient, variables)
                    }
                },
            }),
        })
    }

    return queryClient
}

/**
 * Session configuration stored in queryClient.
 * This serves as the single source of truth for auth/service info
 * used by API request builders.
 */
export interface QueryClientSession {
    authToken: string | undefined
    serviceDict: ServiceDict | undefined
}

/**
 * Query key for session config - using a symbol-like key to avoid collisions
 */
export const SESSION_QUERY_KEY = ['__session__'] as const

/**
 * Query key for job passwords - stored separately from session
 */
export const JOB_PASSWORDS_QUERY_KEY = ['__job_passwords__'] as const

/**
 * Initialize session config in queryClient.
 * Should be called from sessionMiddleware before loaders run.
 *
 * This sets the session data without any caching behavior (gcTime: 0, staleTime: 0)
 * so it acts as simple key-value storage rather than a cached query.
 */
export function initializeQueryClientSession(session: QueryClientSession): void {
    const client = getQueryClient()
    client.setQueryData(SESSION_QUERY_KEY, session)
}

/**
 * Get session config from queryClient.
 * Returns undefined if session hasn't been initialized.
 */
export function getQueryClientSession(): QueryClientSession | undefined {
    const client = getQueryClient()
    return client.getQueryData<QueryClientSession>(SESSION_QUERY_KEY)
}

/**
 * Clear session config from queryClient.
 * Useful for logout scenarios.
 */
export function clearQueryClientSession(): void {
    const client = getQueryClient()
    client.removeQueries({ queryKey: SESSION_QUERY_KEY })
}

/**
 * Check if stored auth credentials exist in the session.
 *
 * This only checks for presence of credentials, NOT validity.
 * The token may be expired - use isSessionExpired from SessionExpiredContext
 * for runtime expiration state.
 *
 * @returns true if authToken and serviceDict are both present
 */
export function hasStoredAuthCredentials(): boolean {
    const session = getQueryClientSession()
    return Boolean(session?.authToken && session?.serviceDict)
}

/**
 * Job password storage in QueryClient.
 * Stores plain password per job for foreign auth.
 */
type JobPasswordsMap = Record<JobID, string>

function getJobPasswordsMap(): JobPasswordsMap {
    const client = getQueryClient()
    return client.getQueryData<JobPasswordsMap>(JOB_PASSWORDS_QUERY_KEY) ?? {}
}

function setJobPasswordsMap(passwords: JobPasswordsMap): void {
    const client = getQueryClient()
    client.setQueryData(JOB_PASSWORDS_QUERY_KEY, passwords)
}

/**
 * Set password for a job.
 * Used to compose foreign auth for API requests:
 * - Query param `foreign-auth`: uses plain password
 * - Authorization header: composes authToken + password
 *
 * @param jobID - The job ID
 * @param password - The password (undefined to clear)
 */
export function setPasswordForJob(jobID: JobID, password?: string): void {
    const passwords = getJobPasswordsMap()
    if (!password) {
        const { [jobID]: _, ...rest } = passwords
        setJobPasswordsMap(rest)
        return
    }
    setJobPasswordsMap({ ...passwords, [jobID]: password })
}

/**
 * Get password for a job.
 *
 * @param jobID - The job ID
 * @returns The stored password, or undefined if not set
 */
export function getPasswordForJob(jobID: JobID): string | undefined {
    return getJobPasswordsMap()[jobID]
}

/**
 * Clear password for a job.
 *
 * @param jobID - The job ID
 */
export function clearPasswordForJob(jobID: JobID): void {
    setPasswordForJob(jobID, undefined)
}
